// File upload routes — every verb declares its parent's grant through the
// ADR 062 — Access Grants gate (BL-1833 PR 2b).
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
import { CREATE_IN_AUCTION_GRANT } from './lots';
// Type-only import (erased at build) — keeps the registry client-bundle-safe.
import type { UploadCompleteDto, UploadUrlDto } from '../../schemas/responses/files';

/**
 * Every parent an upload may attach to, with the grant it requires (BL-1833 PR
 * 2b). The presign step accepts all of them and the complete step all but
 * `invoice`, `settlement` and `terms_proof` (see COMPLETE_ENTITIES), so a caller refused a presign is refused
 * the complete. Its keys must equal `BUCKET_MAP`'s (`admin-isomorphic`, which
 * cannot be imported here); a test in `admin-services` holds them together.
 *
 * Each grant is the PARENT's edit grant, chosen so every UI caller keeps access:
 * Accounting attaches customer documents; a Cataloger to inventory media, staged or not,
 * and to the staged documents of a lot they create in an auction (BL-2349);
 * documents on an existing lot or auction are Admin-only surfaces. The two staged `draft_*`
 * types have no row yet — their tenant is the one the body states.
 */
const UPLOAD_ENTITIES = {
  customer: { table: 'customer', grant: 'customer.update_documents' },
  inventory: { table: 'inventory_item', grant: 'inventory.update' },
  draft_media: { tenantField: 'tenantId', grant: 'inventory.update' },
  auction_lot: { table: 'lot', grant: 'lot.update' },
  auction: { table: 'auction', grant: 'auction.update' },
  auction_hero: { table: 'auction', grant: 'auction.update' },
  // BL-2349: staged by the create-into-auction form only, so it takes that
  // create's grant — a Cataloger attaches documents to the lot they're adding.
  draft_document: { tenantField: 'tenantId', grant: CREATE_IN_AUCTION_GRANT },
  // The logo's entity IS the tenant. Both callers are super-admin pages, so the
  // website grant refuses no UI path.
  tenant_logo: { table: 'tenant', grant: 'settings.update_website' },
  // BL-2217: Accounting and Admin attach files to an invoice. Presign only — the
  // file registers on the invoice, see COMPLETE_ENTITIES.
  invoice: { table: 'invoice', grant: 'invoice.update' },
  // BL-2604: the settlement twin. Presign only — the file registers on the
  // settlement, see COMPLETE_ENTITIES.
  settlement: { table: 'settlement', grant: 'settlement.update' },
  // BL-2590: the signed copy of an auction's terms, keyed on the AUCTION (the
  // attestation it proves is written later, by the add or record request).
  // The add door's grant: Record offline acceptance needs `auction.update`,
  // which no role holds without `auction.approve_bidders`. Presign only — the
  // file registers on the attestation, see COMPLETE_ENTITIES.
  terms_proof: { table: 'auction', grant: 'auction.approve_bidders' },
} as const;

/**
 * The parents the generic complete step accepts: every presign parent except
 * `terms_proof` (it registers on the staff attestation, BL-2590),
 * `customer` (a document saves under its customer,
 * `POST /api/customers/:id/documents`, BL-2791) and
 * `invoice` and `settlement`, whose files register on their record
 * (`POST /api/invoices/:id/attachments`, BL-2217; `POST
 * /api/settlements/:id/attachments`, BL-2604) so the row, its Activity entry
 * and its audit land in one transaction. The complete step would only echo a
 * synthesized attachment for it. A caller refused a presign is still refused
 * the complete, since this is a subset.
 */
const {
  customer: _savedUnderTheCustomer,
  invoice: _registeredOnTheInvoice,
  settlement: _registeredOnTheSettlement,
  terms_proof: _registeredOnTheAttestation,
  ...COMPLETE_ENTITIES
} = UPLOAD_ENTITIES;

/** The polymorphic gate both steps share, over a given parent map. */
function uploadGate<E>(entities: E) {
  return {
    grant: { none: "per entity — the parent entity's grant, see tenant.entities" },
    tenant: {
      from: 'polymorphic',
      typeField: 'entityType',
      idField: 'entityId',
      entities,
    },
  } as const;
}

export const files = {
  // BL-2189: `delete` and `download` were retired. Customer documents are
  // downloaded and deleted under the customer (`customers.id.documents`), and
  // nothing else called either route (inventory photo removal saves with its
  // form, BL-1979). Upload stays here for every entity (PRJ-84 M5).
  // BL-2016: both steps bind their success body to a shared DTO via
  // `respond()` (ADR 027 — API Response Contract).
  upload_complete: defineAdminRoute({
      path: '/api/files/upload-complete',
      params: {},
      methods: { POST: { ...uploadGate(COMPLETE_ENTITIES), response: responseType<UploadCompleteDto>() } },
    }),
  upload_url: defineAdminRoute({
      path: '/api/files/upload-url',
      params: {},
      methods: { POST: { ...uploadGate(UPLOAD_ENTITIES), response: responseType<UploadUrlDto>() } },
    }),
};
